
import ProductDisplayModeToggle from '~/components/ProductDisplayModeToggle'
import ProductListItem from '~/components/ProductListItem'
import VehicleBulbFilters from '~/components/VehicleBulbFilters'
import VehicleBulbListItem from '~/components/VehicleBulbListItem'
import VehicleBulbsPreview from '~/components/VehicleBulbsPreview'
import VehicleHeadlightExcludedProducts from '~/components/VehicleHeadlightExcludedProducts'
import VehicleHeadlightExclusionButton from '~/components/VehicleHeadlightExclusionButton'
import VehicleHeadlightHeroCard from '~/components/VehicleHeadlightHeroCard'
import VehicleHeadlightHeroPinButton from '~/components/VehicleHeadlightHeroPinButton'
import vehicleHeadlightExclusionMixin from '~/mixins/vehicleHeadlightExclusionMixin'
import { hasIntersectionObserver, scrollBehavior } from '~/utils/browserSupport'
import {
  readProductDisplayMode,
  writeProductDisplayMode,
} from '~/utils/productDisplayModeStorage'
import {
  includedSharedRole,
  isKitRevealed,
  isScrollSettled,
  joinTypeNames,
  kitSharedRole,
  sharedRolesInEffect,
} from '~/utils/sharedBulbs'
import {
  BULBS_FETCH_LIMIT,
  bulbBadge,
  bulbFiltersAvailable,
  bulbTechnicalRanges,
  bulbsTeaser,
  bulbsWithoutData,
  filterBulbs,
  formatTeaserRatio,
  hasActiveBulbFilters,
  listBadges,
} from '~/utils/vehicleBulbList'

const KIT_HIGHLIGHT_MS = 2500
const VERSIONS_PER_ROW = 5
const SCROLL_SETTLE_MS = 120
const REVEAL_MAX_MS = 2000
const REVEAL_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20)

export default {
  name: 'VehicleHeadlightTypeCard',
  components: {
    ProductDisplayModeToggle,
    ProductListItem,
    VehicleBulbFilters,
    VehicleBulbListItem,
    VehicleBulbsPreview,
    VehicleHeadlightExcludedProducts,
    VehicleHeadlightExclusionButton,
    VehicleHeadlightHeroCard,
    VehicleHeadlightHeroPinButton,
  },
  mixins: [vehicleHeadlightExclusionMixin],
  props: {
    block: { type: Object, required: true },
    vehicle: { type: Object, required: true },
    heroProducts: { type: Object, default: () => ({}) },
    openBulbsTypeId: { type: Number, default: null },
    selectedDetailId: { type: Number, default: null },
    sharedGroups: { type: Array, default: () => [] },
    sharedDetails: { type: Object, default: () => ({}) },
    sharedKitComparison: { type: Object, default: null },
    vehicleLabel: { type: String, default: '' },
  },
  data() {
    return {
      packDiagnosticBusy: null,
      heroPinPendingId: null,
      heroPinFailedId: null,
      bulbsVisible: false,
      bulbsInstantClose: false,
      bulbsLoading: false,
      bulbsFetched: false,
      expandTracked: false,
      bulbs: [],
      bulbBadges: listBadges(null),
      bulbFilters: {},
      bulbsDisplayMode: 'classic',
      bulbsFetchToken: 0,
      kitHighlighted: false,
    }
  },
  watch: {
    bulbsDisplayMode(val) {
      writeProductDisplayMode(val)
    },
    openBulbsTypeId(val) {
      if (val !== this.block.headlight_type.id) {
        this.bulbsVisible = false
      }
    },
    selectedDetailId() {
      this.bulbs = []
      this.bulbBadges = listBadges(null)
      this.bulbFilters = {}
      this.bulbsFetched = false
      if (!this.bulbsVisible) return
      if (this.showBulbsSection) {
        this.fetchBulbs()
        return
      }
      this.bulbsVisible = false
    },
  },
  created() {
    this.revealObserver = null
    this.revealTimers = []
    this.hidePending = false
  },
  mounted() {
    this.restoreDisplayMode()
  },
  beforeDestroy() {
    this.clearReveal()
  },
  computed: {
    anchorId() {
      return this.typeAnchorId(this.block.headlight_type)
    },
    iconSrc() {
      const image = this.block.headlight_type.images?.[0]
      if (!image) return null
      return this.$cropUrl(image, 96, 96, image.extension, 86)
    },
    details() {
      return this.block.details || []
    },
    selectedDetail() {
      if (this.selectedDetailId === null) return null
      return (
        this.details.find(d => d.id === this.selectedDetailId) ||
        this.details[0] ||
        null
      )
    },
    selectedCategories() {
      return this.selectedDetail?.headlight_categories || []
    },
    selectedCount() {
      return this.selectedDetail?.products_count || 0
    },
    heroProduct() {
      if (!this.selectedDetail?.hero_product_id) return null
      const p = this.heroProducts[this.selectedDetail.hero_product_id]
      return p && p.variant ? p : null
    },
    heroSource() {
      return this.heroProduct ? this.selectedDetail.hero_source : null
    },
    heroIsPinned() {
      return ['pinned', 'pinned_pack'].includes(this.heroSource)
    },
    pinnedProductId() {
      return this.selectedDetail?.pinned_product_id || null
    },
    heroPinVersion() {
      if (this.selectedDetailId === null) return null
      return {
        headlight_type_id: this.block.headlight_type.id,
        headlight_vehicle_detail_id: this.selectedDetailId,
      }
    },
    isAdmin() {
      return (
        this.$auth.loggedIn && this.$auth.user?.role?.identifier === 'admin'
      )
    },
    packDiagnostic() {
      return (this.details || []).flatMap(detail =>
        (detail.pack_diagnostic || []).map(row => ({
          ...row,
          candidates: Array.isArray(row.candidates) ? row.candidates : [],
          detailName: detail.id === -1 ? null : detail.name,
        }))
      )
    },
    sharedRoles() {
      return sharedRolesInEffect(
        this.sharedGroups,
        this.block.headlight_type.id,
        this.selectedDetail,
        this.sharedDetails
      )
    },
    sharedIncludedRole() {
      return includedSharedRole(
        this.sharedGroups,
        this.block.headlight_type.id,
        this.selectedDetail,
        this.sharedDetails
      )
    },
    heroSharedRole() {
      if (!this.heroProduct || this.selectedCategories.length !== 1) {
        return null
      }
      return kitSharedRole(
        this.sharedGroups,
        this.block.headlight_type.id,
        this.selectedDetail,
        this.sharedDetails
      )
    },
    sharedInfoRoles() {
      if (this.sharedIncludedRole) return []
      const heroCategoryId = this.heroSharedRole?.category.id
      return this.sharedRoles.filter(
        role => role.category.id !== heroCategoryId
      )
    },
    sharedVehicleLabel() {
      return this.vehicleLabel || this.vehicle.name
    },
    kitHighlightClass() {
      return this.kitHighlighted
        ? 'kit-highlight tw-ring-[3px] tw-ring-[#7ed321] tw-shadow-[0_0_22px_4px_rgba(126,211,33,0.45)]'
        : null
    },
    sharedKitCompareLabel() {
      const comparison = this.sharedKitComparison
      if (!comparison) return ''

      return comparison.socket
        ? this.$tc(
            'components.vehicleHeadlightHeroCard.compareLinkWithSocket',
            comparison.count,
            { count: comparison.count, socket: comparison.socket }
          )
        : this.$tc(
            'components.vehicleHeadlightHeroCard.compareLink',
            comparison.count,
            { count: comparison.count }
          )
    },
    showBulbsSection() {
      if (this.sharedIncludedRole) return false
      if (this.selectedCount > 1) return true
      if (this.selectedDetail?.excluded_products_count) return true

      return this.selectedCount === 1 && !this.heroProduct
    },
    bulbsCollapseId() {
      return `bulbs-${this.block.headlight_type.id}`
    },
    bulbsSocket() {
      return this.selectedCategories.length === 1
        ? this.selectedCategories[0].name
        : null
    },
    bulbsButtonLabel() {
      const socket = this.bulbsSocket

      if (socket) {
        return this.$tc(
          'components.vehicleHeadlightTypeCard.compareBulbsWithSocket',
          this.selectedCount,
          { count: this.selectedCount, socket }
        )
      }

      return this.$tc(
        'components.vehicleHeadlightTypeCard.compareBulbs',
        this.selectedCount,
        { count: this.selectedCount }
      )
    },
    bulbsTeaserText() {
      if (this.selectedCount < 2) return ''

      const { ratio, tints } = bulbsTeaser(this.selectedDetail?.bulbs_teaser)

      return [
        ratio !== null
          ? this.$t('components.vehicleHeadlightTypeCard.teaserBrighter', {
              ratio: formatTeaserRatio(ratio, this.$i18n.locale),
            })
          : null,
        tints !== null
          ? this.$tc('components.vehicleHeadlightTypeCard.teaserTints', tints, {
              count: tints,
            })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    },
    heroCompareCount() {
      return this.heroProduct && this.showBulbsSection ? this.selectedCount : 0
    },
    bulbsPreview() {
      const preview = this.selectedDetail?.bulbs_preview

      return this.heroCompareCount &&
        !this.bulbsVisible &&
        preview?.products?.length
        ? preview
        : null
    },
    bulbRanges() {
      return bulbTechnicalRanges(this.bulbs)
    },
    bulbFiltersEnabled() {
      return bulbFiltersAvailable(this.bulbs, this.bulbRanges)
    },
    bulbFiltersActive() {
      return hasActiveBulbFilters(this.bulbFilters)
    },
    visibleBulbs() {
      return filterBulbs(this.bulbs, this.bulbFilters)
    },
    bulbsMissingData() {
      return bulbsWithoutData(this.bulbs, this.bulbFilters)
    },
    hasMultipleDetails() {
      return this.details.length >= 2
    },
    versionsGridStyle() {
      const count = this.details.length
      if (count <= VERSIONS_PER_ROW) return null

      const columns = Math.ceil(count / Math.ceil(count / VERSIONS_PER_ROW))

      return { maxWidth: `calc(${columns} * 15rem + ${columns - 1} * 0.75rem)` }
    },
    singleDetailCaption() {
      return this.details[0] && this.details[0].id !== -1
        ? this.details[0].name
        : null
    },
    heroCartMetadata() {
      const meta = {
        headlight_type_id: this.block.headlight_type.id,
        vehicle_id: this.vehicle.id,
      }
      if (this.selectedDetailId !== -1) {
        meta.headlight_vehicle_detail_id = this.selectedDetailId
      }
      return meta
    },
    headlightExclusionVehicleId() {
      return this.vehicle.id
    },
    headlightExclusionVersion() {
      if (!this.bulbsFetched || this.selectedDetailId === null) return null
      return {
        headlight_type_id: this.block.headlight_type.id,
        headlight_vehicle_detail_id: this.selectedDetailId,
      }
    },
    bulbsCartMetadata() {
      const meta = {
        headlight_type_id: this.block.headlight_type.id,
        vehicle_id: this.vehicle.id,
      }
      if (this.selectedDetailId !== -1) {
        meta.headlight_vehicle_detail_id = this.selectedDetailId
      }
      return meta
    },
    filteredViewLink() {
      return this.localePath({
        name: 'recherche-led-accessoires-brand-type-typeid',
        params: this.$route.params,
        query: {
          type: String(this.block.headlight_type.id),
          technologie: String(this.selectedDetailId),
        },
      })
    },
  },
  methods: {
    markPack(row) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticMarkConfirm',
          {
            pack: row.pack_id,
            line: row.line_id,
          }
        ),
        () =>
          this.$axios.$post(`/products/${row.pack_id}/hero-diagnostic/mark`, {
            line_ids: [row.line_id],
          })
      )
    },
    choosePack(row, candidate) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticChooseConfirm',
          {
            pack: candidate.id,
            name: candidate.name,
            line: row.line_id,
          }
        ),
        () =>
          this.$axios.$post(`/products/${candidate.id}/hero-diagnostic/mark`, {
            line_ids: [row.line_id],
            chosen: true,
          })
      )
    },
    attachPack(row, candidate) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticAttachConfirm',
          {
            pack: candidate.id,
            stale: row.pack_id,
          }
        ),
        () =>
          this.$axios.$post(`/products/${candidate.id}/hero-diagnostic/mark`, {
            line_ids: [row.line_id],
            chosen: row.candidates.length > 1,
          }),
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticAttachConfirmDetail',
          {
            name: candidate.name,
            staleName: row.pack_name,
            line: this.packDiagnosticLineLabel(row),
          }
        )
      )
    },
    replacePack(row, candidate) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticReplaceConfirm',
          {
            pack: candidate.id,
            stale: row.pack_id,
          }
        ),
        () =>
          this.$axios.$post(`/products/${candidate.id}/hero-diagnostic/mark`, {
            line_ids: [row.line_id],
            chosen: true,
          }),
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticAttachConfirmDetail',
          {
            name: candidate.name,
            staleName: row.pack_name,
            line: this.packDiagnosticLineLabel(row),
          }
        )
      )
    },
    detachPack(row) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticDetachConfirm',
          { pack: row.pack_id }
        ),
        () =>
          this.$axios.$post(
            `/products/${row.pack_id}/hero-diagnostic/release`,
            {
              line_ids: [row.line_id],
            }
          ),
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticDetachConfirmDetail',
          {
            name: row.pack_name,
            line: this.packDiagnosticLineLabel(row),
          }
        )
      )
    },
    fillPackSpecs(row) {
      return this.runPackDiagnosticAction(
        row,
        this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticFillConfirm',
          {
            pack: row.pack_id,
            watts: row.suggested_watts,
            lumens: row.suggested_lumens,
          }
        ),
        () =>
          this.$axios.$post('/vehicle-pack-missing-specs/apply', {
            variant_ids: [row.variant_id],
          })
      )
    },
    async runPackDiagnosticAction(row, question, action, detail = null) {
      const answer = await this.$swal({
        title: question,
        text: detail,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticConfirm'
        ),
        cancelButtonText: this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticCancel'
        ),
      })

      if (!answer.isConfirmed) return

      this.packDiagnosticBusy = row.line_id

      try {
        const result = await action()

        if (this.packDiagnosticSkipped(result)) {
          this.$swal({
            title: this.$t(
              'components.vehicleHeadlightTypeCard.packDiagnosticSkipped'
            ),
            icon: 'warning',
          })
        }

        this.$emit('refresh')
        if (this.bulbsFetched) this.fetchBulbs()
      } catch (error) {
        this.$swalErrorMessage(error)
      } finally {
        this.packDiagnosticBusy = null
      }
    },
    packDiagnosticSkipped(result) {
      if (!result || !Array.isArray(result.skipped) || !result.skipped.length) {
        return false
      }

      return !(result.marked || result.released || []).length
    },
    isStalePackRow(row) {
      return ['not_covered', 'not_included'].includes(row.reason)
    },
    isReplaceablePackRow(row) {
      return (
        Boolean(row.pack_id) &&
        !this.isStalePackRow(row) &&
        row.candidates.length > 0
      )
    },
    packDiagnosticLineLabel(row) {
      return [
        row.detailName,
        row.headlight_category && row.headlight_category.name,
      ]
        .filter(Boolean)
        .join(' · ')
    },
    packDiagnosticLinks(row) {
      return row.pack_id
        ? [...row.candidates, { id: row.pack_id, name: row.pack_name }]
        : []
    },
    packDiagnosticStaleLabel(row) {
      const undeclared = row.undeclared || []
      let key = 'packDiagnosticStaleVehicle'

      if (row.reason === 'not_covered') {
        key =
          undeclared.length === 2
            ? 'packDiagnosticStaleCouple'
            : undeclared[0] === 'headlight_type'
            ? 'packDiagnosticStaleType'
            : 'packDiagnosticStaleCulot'
      }

      return this.$t(`components.vehicleHeadlightTypeCard.${key}`, {
        pack: row.pack_id,
        type: this.block.headlight_type.name,
        culot: row.headlight_category && row.headlight_category.name,
      })
    },
    packDiagnosticStaleHint(row) {
      if (row.candidates.length === 1) {
        return this.$t(
          'components.vehicleHeadlightTypeCard.packDiagnosticStaleReplacement',
          {
            pack: row.candidates[0].id,
            name: row.candidates[0].name,
          }
        )
      }

      if (row.candidates.length > 1) {
        return this.$tc(
          'components.vehicleHeadlightTypeCard.packDiagnosticStaleSeveral',
          row.candidates.length,
          { count: row.candidates.length }
        )
      }

      return this.$t(
        'components.vehicleHeadlightTypeCard.packDiagnosticStaleNone'
      )
    },
    packDiagnosticLabel(row) {
      if (this.isStalePackRow(row)) return this.packDiagnosticStaleLabel(row)

      if (row.reason === 'several_candidates') {
        const count = (row.candidates || []).length

        return this.$tc(
          'components.vehicleHeadlightTypeCard.packDiagnosticSeveralCandidates',
          count,
          { count, line: row.line_id }
        )
      }

      const keys = {
        line_free: 'packDiagnosticLineFree',
        shared_across_versions: 'packDiagnosticSharedAcrossVersions',
        missing_specs: 'packDiagnosticMissingSpecs',
        not_orderable: 'packDiagnosticNotOrderable',
        product_inactive: 'packDiagnosticProductInactive',
        product_hidden: 'packDiagnosticProductHidden',
        variant_missing: 'packDiagnosticVariantMissing',
        product_missing: 'packDiagnosticProductMissing',
      }

      if (!keys[row.reason]) return row.reason

      return this.$t(
        `components.vehicleHeadlightTypeCard.${keys[row.reason]}`,
        {
          pack: row.pack_id,
          line: row.line_id,
        }
      )
    },
    restoreDisplayMode() {
      const saved = readProductDisplayMode()
      if (saved) this.bulbsDisplayMode = saved
    },
    async fetchBulbs() {
      const token = ++this.bulbsFetchToken
      this.bulbsFetched = true
      this.bulbsLoading = true
      this.bulbFilters = {}
      try {
        const params = {
          fill: true,
          limit: BULBS_FETCH_LIMIT,
          headlight_vehicle_detail_id: this.selectedDetailId,
        }
        const response =
          await this.$productService.fetchConfiguratorVehicleHeadlightTypeProducts(
            this.vehicle.id,
            this.block.headlight_type.id,
            params
          )
        if (token !== this.bulbsFetchToken) return
        this.bulbs = response?.data || []
        this.bulbBadges = listBadges(response?.meta)
      } catch (error) {
        if (token !== this.bulbsFetchToken) return
        this.bulbs = []
        this.bulbBadges = listBadges(null)
      } finally {
        if (token === this.bulbsFetchToken) {
          this.bulbsLoading = false
        }
      }
    },
    async toggleBulbs() {
      if (this.hidePending) {
        this.hidePending = false
        this.clearReveal()
      }
      this.bulbsVisible = !this.bulbsVisible
      if (!this.bulbsVisible) return

      this.restoreDisplayMode()
      this.$emit('bulbs-open', this.block.headlight_type.id)

      if (!this.expandTracked) {
        this.$trackVehicleEvent(this.vehicle.id, 'select_lighting_type', {
          headlight_type_id: this.block.headlight_type.id,
        })
        this.expandTracked = true
      }

      if (this.bulbsFetched) return
      await this.fetchBulbs()
    },
    closeBulbsInstantly() {
      if (!this.bulbsVisible) return
      this.bulbsInstantClose = true
      this.bulbsVisible = false
    },
    onHeadlightProductExcluded(product) {
      this.bulbs = this.bulbs.filter(bulb => bulb.id !== product.id)
      this.$emit('refresh')
    },
    onHeadlightProductRestored(product) {
      this.bulbs = [...this.bulbs, product]
      this.$emit('refresh')
    },
    toggleHeroPin(product) {
      if (this.pinnedProductId === product.id) return this.unpinHero()

      return this.updateHeroPin(product.id, () =>
        this.$vehicleService.pinHeadlightHero(this.vehicle.id, {
          product_id: product.id,
          ...this.heroPinVersion,
        })
      )
    },
    unpinHero() {
      return this.updateHeroPin(this.pinnedProductId, () =>
        this.$vehicleService.unpinHeadlightHero(
          this.vehicle.id,
          this.heroPinVersion
        )
      )
    },
    async updateHeroPin(productId, request) {
      if (this.heroPinPendingId || !this.heroPinVersion) return

      this.heroPinPendingId = productId
      this.heroPinFailedId = null

      try {
        await request()
        this.$emit('refresh')
      } catch {
        this.heroPinFailedId = productId
      } finally {
        this.heroPinPendingId = null
      }
    },
    badgeOf(bulb) {
      return bulbBadge(this.bulbBadges, bulb.id)
    },
    compareBulbs() {
      if (!this.bulbsVisible) this.toggleBulbs()
      this.$nextTick(() => {
        const section = this.$refs.bulbsSection
        if (section)
          section.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
      })
    },
    hideBulbsToHero() {
      const hero = this.$refs.heroCard
      this.clearReveal()
      if (!hero) {
        this.bulbsVisible = false
        return
      }

      hero.$el.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' })
      this.hidePending = true
      this.whenArrived(null, () => {
        this.hidePending = false
        this.bulbsVisible = false
        hero.focusCompare()
      })
    },
    socketCountParts(detail) {
      return this.$tc(
        'components.vehicleHeadlightTypeCard.bulbsCountWithSocket',
        detail.products_count,
        { count: detail.products_count, socket: '§' }
      ).split('§')
    },
    selectDetail(detailId) {
      const changed = this.selectedDetailId !== detailId
      this.$emit('select-detail', detailId)
      if (!changed) return
      this.$trackVehicleEvent(this.vehicle.id, 'select_technology', {
        headlight_type_id: this.block.headlight_type.id,
        metadata: {
          headlight_vehicle_detail_id: detailId,
        },
      })
    },
    joinTypeNames,
    typeAnchorId(type) {
      return `feux-${this.$slugify(type.name).toLowerCase() || type.id}`
    },
    showSharedKit() {
      if (!this.sharedIncludedRole) return
      this.$emit('show-shared-kit', this.selectedDetail.id)
    },
    compareSharedKit() {
      if (!this.sharedIncludedRole) return
      this.$emit('compare-shared-kit', this.selectedDetail.id)
    },
    revealKit() {
      this.clearReveal()
      this.$el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
      this.whenArrived(this.$refs.heroCard?.$el || this.$el, this.highlightKit)
    },
    revealBulbs() {
      const section = this.$refs.bulbsSection
      if (!section) return

      this.clearReveal()
      if (!this.bulbsFetched) this.fetchBulbs()
      section.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
      this.whenArrived(null, this.compareBulbs)
    },
    whenArrived(target, callback) {
      const arrive = () => {
        this.clearReveal()
        callback()
      }

      if (target && hasIntersectionObserver()) {
        this.revealObserver = new IntersectionObserver(
          entries => {
            if (entries.some(entry => isKitRevealed(entry))) arrive()
          },
          { threshold: REVEAL_THRESHOLDS }
        )
        this.revealObserver.observe(target)
      }

      let lastScrollY = window.scrollY
      let moved = false
      let stillChecks = 0
      const checkSettled = () => {
        if (window.scrollY === lastScrollY) {
          stillChecks += 1
        } else {
          moved = true
          stillChecks = 0
          lastScrollY = window.scrollY
        }
        if (isScrollSettled(moved, stillChecks)) {
          arrive()
          return
        }
        this.revealTimers.push(setTimeout(checkSettled, SCROLL_SETTLE_MS))
      }

      this.revealTimers.push(
        setTimeout(checkSettled, SCROLL_SETTLE_MS),
        setTimeout(arrive, REVEAL_MAX_MS)
      )
    },
    highlightKit() {
      this.kitHighlighted = true
      this.revealTimers.push(
        setTimeout(() => {
          this.kitHighlighted = false
        }, KIT_HIGHLIGHT_MS)
      )
    },
    clearReveal() {
      if (this.revealObserver) {
        this.revealObserver.disconnect()
        this.revealObserver = null
      }
      this.revealTimers.forEach(clearTimeout)
      this.revealTimers = []
      this.kitHighlighted = false
    },
  },
}
